import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchRunlifeDashboardDataService,
  fetchInstallationYearsService,
  fetchFilterDatesService,
} from '../../services/runlifeDashboard/RunlifeDashboardService';
import { RunLifeSummaryKeys } from './widgets/summary';
import { runLifeOverallStats } from './widgets/overall-stats';

export interface RunlifeGridState<T = any> {
  data: T[];
}

export interface FieldStatistic {
  Field: string;
  TotalWells: number;
  TotalInstallations: number;
  TotalRunningUnits: number;
  TotalFailures: number;
  AverageRunlife: number;
  AverageRunlifeFailedUnits: number;
  AverageRunlifePulledUnits: number;
  AverageRunlifeRunningUnits: number;
  Formation: string | null;
  MeanTimeBetweenFailure: number;
  TotalFailuresWithinDays: number;
  TotalPulls: number;
}

export interface BoxPlotChartStatistic {
  X: number;
  Minimum: number;
  FirstQuartile: number;
  ThirdQuartile: number;
  Maximum: number;
  Median: number;
  Mean: number;
  Outliers: number[];
}
export interface FormationStatistics {
  Formation: string;
  TotalInstallations: number;
  TotalRunningUnits: number;
  TotalFailures: number;
  AverageRunlife: number;
  AverageRunlifeFailedUnits: number;
  AverageRunlifePulledUnits: number;
  AverageRunlifeRunningUnits: number;
  MeanTimeBetweenFailure: number;
  TotalFailuresWithinDays: number;
  TotalPulls: number;
}

export interface RunlifeDashboardState {
  manufacturerCols: any[];
  ManufacturerStatistics: RunlifeGridState;
  FieldStatistics: FieldStatistic[];
  FormationStatistics: FormationStatistics[];
  BoxPlotChartStatistics: BoxPlotChartStatistic[];
  summaryCount?: Record<RunLifeSummaryKeys, number>;
  overallStats?: any[];
  loading: boolean;
  error?: string | null;
  installationYears?: number[];
  filterDates?: { Key: string; Value: string }[];
  selectedYear?: number;
  startDate?: string | null;
  endDate?: string | null;
  selectedDateType?: string | null;
}

const initialState: RunlifeDashboardState = {
  manufacturerCols: [],
  ManufacturerStatistics: { data: [] },
  FieldStatistics: [],
  BoxPlotChartStatistics: [],
  FormationStatistics: [],
  loading: false,
  error: null,
  installationYears: [],
  filterDates: [],
  selectedYear: undefined,
  startDate: null,
  endDate: null,
  selectedDateType: undefined,
};

/* -------------------- Utility: normalize Dates -------------------- */

/**
 * Recursively walk an object/array and convert Date instances to ISO strings.
 * Leaves primitives unchanged.
 */
const normalizeDates = (value: any): any => {
  if (value === null || value === undefined) return value;

  // If it's a Date instance -> convert to ISO string
  if (value instanceof Date) {
    return value.toISOString();
  }

  // If it's a primitive (string/number/boolean) -> return as is
  if (typeof value !== 'object') {
    return value;
  }

  // If it's an array -> map each item recursively
  if (Array.isArray(value)) {
    return value.map((item) => normalizeDates(item));
  }

  // Otherwise it's an object -> map keys recursively
  const copy: any = {};
  for (const [k, v] of Object.entries(value)) {
    // convert Date objects to ISO, and recursively process nested arrays/objects
    if (v instanceof Date) copy[k] = v.toISOString();
    else copy[k] = normalizeDates(v);
  }
  return copy;
};

/**
 * fetchRunlifeDashboard: accepts optional params { year?: number; dateType?: string; startDate?: string; endDate?: string }
 * Keep backward compatibility by allowing undefined.
 */
export const fetchRunlifeDashboard = createAsyncThunk(
  'runlifeDashboard/fetch',
  async (params: { year?: number; dateType?: string; startDate?: string; endDate?: string } | undefined, thunkAPI) => {
    try {
      const data = await fetchRunlifeDashboardDataService(params);
      return data;
    } catch (err: any) {
      const message = err?.response?.data?.message || err.message || 'Failed to fetch Runlife dashboard data';
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const fetchInstallationYears = createAsyncThunk('runlifeDashboard/fetchYears', async (_, thunkAPI) => {
  try {
    const data = await fetchInstallationYearsService();
    return data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err?.message || 'Failed to fetch installation years');
  }
});

export const fetchFilterDates = createAsyncThunk('runlifeDashboard/fetchFilterDates', async (_, thunkAPI) => {
  try {
    const data = await fetchFilterDatesService();
    return data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err?.message || 'Failed to fetch filter dates');
  }
});

const runlifeDashboardSlice = createSlice({
  name: 'runlifeDashboard',
  initialState,
  reducers: {
    clearRunlifeDashboardError(state) {
      state.error = null;
    },
    setManufacturerStatistics(state, action: PayloadAction<any[]>) {
      state.ManufacturerStatistics.data = action.payload ?? [];
    },
    setFieldStatistics(state, action: PayloadAction<FieldStatistic[]>) {
      state.FieldStatistics = action.payload ?? [];
    },
    setBoxPlotChartStatistics(state, action: PayloadAction<BoxPlotChartStatistic[]>) {
      state.BoxPlotChartStatistics = action.payload ?? [];
    },
    setInstallationYears(state, action: PayloadAction<number[]>) {
      state.installationYears = action.payload ?? [];
    },
    setFilterDates(state, action: PayloadAction<{ Key: string; Value: string }[]>) {
      state.filterDates = action.payload ?? [];
    },
    setDateFilters(
      state,
      action: PayloadAction<{
        selectedYear?: number;
        startDate?: string | null;
        endDate?: string | null;
        selectedDateType?: string | null;
      }>,
    ) {
      state.selectedYear = action.payload.selectedYear;
      state.startDate = action.payload.startDate;
      state.endDate = action.payload.endDate;
      state.selectedDateType = action.payload.selectedDateType;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetch dashboard
      .addCase(fetchRunlifeDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRunlifeDashboard.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = null;

        const payload = action.payload || {};

        // Normalize any Date objects in the payload arrays before storing to state
        try {
          state.ManufacturerStatistics.data = normalizeDates(payload?.ManufacturerStatistics ?? []);
          state.FieldStatistics = normalizeDates(payload?.FieldStatistics ?? []);
          state.FormationStatistics = normalizeDates(payload?.FormationStatistics ?? []);
          state.BoxPlotChartStatistics = normalizeDates(payload?.BoxPlotChartStatistics ?? []);
        } catch (e) {
          // if normalization fails for any reason, fall back to raw assignment
          state.ManufacturerStatistics.data = payload?.ManufacturerStatistics ?? [];
          state.FieldStatistics = payload?.FieldStatistics ?? [];
          state.FormationStatistics = payload?.FormationStatistics ?? [];
          state.BoxPlotChartStatistics = payload?.BoxPlotChartStatistics ?? [];
        }

        // summary count
        const count: any = {};
        Object.entries(RunLifeSummaryKeys).map(([key]) => {
          count[key as RunLifeSummaryKeys] = payload[key];
        });
        state.summaryCount = count;

        // overall stats mapping
        state.overallStats = runLifeOverallStats.map((item) => ({ ...item, count: payload[item.key] }));
      })
      .addCase(fetchRunlifeDashboard.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload || action.error?.message || 'Failed to fetch Runlife dashboard data';
      })

      // installation years
      .addCase(fetchInstallationYears.fulfilled, (state, action: PayloadAction<number[]>) => {
        state.installationYears = action.payload ?? [];
      })
      .addCase(fetchInstallationYears.rejected, (state) => {
        state.installationYears = [];
      })

      // filter dates
      .addCase(fetchFilterDates.fulfilled, (state, action: PayloadAction<{ Key: string; Value: string }[]>) => {
        state.filterDates = action.payload ?? [];
      })
      .addCase(fetchFilterDates.rejected, (state) => {
        state.filterDates = [];
      });
  },
});

export const {
  clearRunlifeDashboardError,
  setManufacturerStatistics,
  setFieldStatistics,
  setBoxPlotChartStatistics,
  setInstallationYears,
  setFilterDates,
  setDateFilters,
} = runlifeDashboardSlice.actions;

export default runlifeDashboardSlice.reducer;

/* -------------------- Selectors -------------------- */

/* basic state selectors */
export const selectRunlifeDashboardLoading = (state: any) => state.runlifeDashboard?.loading ?? false;
export const selectRunlifeDashboardError = (state: any) => state.runlifeDashboard?.error ?? null;
export const selectManufacturerStatistics = (state: any) => state.runlifeDashboard?.ManufacturerStatistics?.data ?? [];
export const selectFieldStatistics = (state: any) => state.runlifeDashboard?.FieldStatistics ?? [];
export const selectSelectedYear = (state: any) => state.runlifeDashboard?.selectedYear;
export const selectStartDate = (state: any) => state.runlifeDashboard?.startDate;
export const selectEndDate = (state: any) => state.runlifeDashboard?.endDate;
export const selectSelectedDateType = (state: any) => state.runlifeDashboard?.selectedDateType;
export const selectBoxPlotChartStatistics = (state: any): BoxPlotChartStatistic[] =>
  (state.runlifeDashboard?.BoxPlotChartStatistics as BoxPlotChartStatistic[]) ?? [];

/* derived selectors that other widgets import (EXACT names expected by your widgets) */

/**
 * selectRunLifeSummary
 * used by summary widget — return a map of summary key -> number
 */
export const selectRunLifeSummary = (state: any): Record<string, number> =>
  (state.runlifeDashboard?.summaryCount as Record<string, number>) ?? {};

/**
 * selectRunLifeOverallStats
 * used by overall-stats widget (DataTableGrid expects an array)
 */
export const selectRunLifeOverallStats = (state: any): any[] => state.runlifeDashboard?.overallStats ?? [];

/**
 * selectRunLifeFormationStats
 * used by formation-stats widget (DataTableGrid expects array of formation stats)
 */
export const selectRunLifeFormationStats = (state: any): FormationStatistics[] =>
  (state.runlifeDashboard?.FormationStatistics as FormationStatistics[]) ?? [];

/* other selectors used across the dashboard */
export const selectManufacturerInstallation = (state: any) => {
  const manufacturerStatistics = state.runlifeDashboard?.ManufacturerStatistics?.data ?? [];
  return Array.isArray(manufacturerStatistics) && manufacturerStatistics.length > 0
    ? manufacturerStatistics.map((manufacturer: any) => ({
        Manufacturer: manufacturer.Manufacturer || 'Unknown',
        TotalInstallations: manufacturer.TotalInstallations || 0,
        TotalRunningUnits: manufacturer.TotalRunningUnits || 0,
        TotalFailures: manufacturer.TotalFailures || 0,
        TotalFailuresWithinDays: manufacturer.TotalFailuresWithinDays || 0,
        TotalPulls: manufacturer.TotalPulls || 0,
        AverageRunlife: manufacturer.AverageRunlife || 0,
        AverageRunlifeRunningUnits: manufacturer.AverageRunlifeRunningUnits || 0,
        AverageRunlifeFailedUnits: manufacturer.AverageRunlifeFailedUnits || 0,
        AverageRunlifePulledUnits: manufacturer.AverageRunlifePulledUnits || 0,
        MeanTimeBetweenFailure: manufacturer.MeanTimeBetweenFailure || 0,
      }))
    : [];
};

export const selectManufacturerRunlife = (state: any) => {
  const manufacturerStatistics = state.runlifeDashboard?.ManufacturerStatistics?.data ?? [];
  return Array.isArray(manufacturerStatistics) && manufacturerStatistics.length > 0
    ? manufacturerStatistics.map((manufacturer: any) => ({
        Manufacturer: manufacturer.Manufacturer || 'Unknown',
        AverageRunlifeRunningUnits: manufacturer.AverageRunlifeRunningUnits || 0,
        AverageRunlife: manufacturer.AverageRunlife || 0,
        AverageRunlifeFailedUnits: manufacturer.AverageRunlifeFailedUnits || 0,
        MeanTimeBetweenFailure: manufacturer.MeanTimeBetweenFailure || 0,
      }))
    : [];
};

export const selectFieldInstallation = (state: any) => {
  const fieldStatistics = state.runlifeDashboard?.FieldStatistics ?? [];
  return Array.isArray(fieldStatistics) && fieldStatistics.length > 0
    ? fieldStatistics.map((field: FieldStatistic) => ({
        Field: field.Field || 'Unknown',
        TotalInstallations: field.TotalInstallations || 0,
        TotalRunningUnits: field.TotalRunningUnits || 0,
        TotalFailures: field.TotalFailures || 0,
        TotalFailuresWithinDays: field.TotalFailuresWithinDays || 0,
        TotalPulls: field.TotalPulls || 0,
      }))
    : [];
};

export const selectAverageRunlifeByField = (state: any) => {
  const fieldStatistics = state.runlifeDashboard?.FieldStatistics ?? [];
  return Array.isArray(fieldStatistics) && fieldStatistics.length > 0
    ? fieldStatistics.map((field: FieldStatistic) => ({
        Field: field.Field || 'Unknown',
        AverageRunlifeRunningUnits: field.AverageRunlifeRunningUnits || 0,
        AverageRunlife: field.AverageRunlife || 0,
        AverageRunlifeFailedUnits: field.AverageRunlifeFailedUnits || 0,
        MeanTimeBetweenFailure: field.MeanTimeBetweenFailure || 0,
      }))
    : [];
};
export const selectRunLifeManufactorerStatsCols = (state: any) => state.runlifeDashboard.manufacturerCols;

/* selectors for dropdowns */
export const selectInstallationYears = (state: any) => state.runlifeDashboard?.installationYears ?? [];
export const selectFilterDates = (state: any) => state.runlifeDashboard?.filterDates ?? [];
